import { InventoryChangeKind } from '@thinkalpha/locates-ws-client';
import type { InventoryChangeEntry } from 'src/admin/models/PoolManagementModel';
import { escapeCsvCell } from 'src/admin/models/PoolManagementModel/upload/csvEscape';

export interface ChangeLogCsvColumnLabels {
    timestamp: string;
    user: string;
    symbol: string;
    action: string;
    quantity: string;
    price: string;
    orderId: string;
}

export interface CreateChangeLogCsvOptions {
    /**
     * Human labels for the header row. Pulled out as a parameter (rather
     * than hard-coded English) so the caller can localize via i18n
     * without re-importing this module.
     */
    columnLabels: ChangeLogCsvColumnLabels;
    /**
     * Formats `entry.timestamp` (ms-since-epoch) into a display string.
     * Defaults to ISO-8601 UTC so the file opens predictably in Excel /
     * Sheets regardless of locale; callers that prefer the user's local
     * format can pass their own.
     */
    formatTimestamp?: (timestampMs: number) => string;
}

/**
 * Serialize a list of `InventoryChangeEntry` rows to a CSV string.
 *
 * Pure: no clock reads, no DOM access, no i18n imports. The caller
 * supplies the localized header labels (and optionally a timestamp
 * formatter) so the builder stays trivially unit-testable. The action
 * column carries the backend change kind verbatim (stable strings) —
 * except availability changes, whose direction lives in neither
 * quantity nor price, so the cell reads `availability:published` /
 * `availability:suspended` to keep the two distinguishable in the
 * exported audit trail. Quantity keeps its sign (a signed delta for
 * increments) and both quantity and price serialize as empty cells
 * when the change kind does not affect them (`null`).
 */
export function createChangeLogCsv(
    entries: ReadonlyArray<InventoryChangeEntry>,
    options: CreateChangeLogCsvOptions,
): string {
    const { columnLabels, formatTimestamp = defaultFormatTimestamp } = options;
    const header = [
        escapeCsvCell(columnLabels.timestamp),
        escapeCsvCell(columnLabels.user),
        escapeCsvCell(columnLabels.symbol),
        escapeCsvCell(columnLabels.action),
        escapeCsvCell(columnLabels.quantity),
        escapeCsvCell(columnLabels.price),
        escapeCsvCell(columnLabels.orderId),
    ].join(',');
    const lines = [header];
    for (const entry of entries) {
        lines.push(
            [
                escapeCsvCell(formatTimestamp(entry.timestamp)),
                escapeCsvCell(entry.userName),
                escapeCsvCell(entry.symbol),
                escapeCsvCell(actionCell(entry)),
                escapeCsvCell(entry.quantity ?? ''),
                escapeCsvCell(entry.price ?? ''),
                escapeCsvCell(entry.orderId ?? ''),
            ].join(','),
        );
    }
    return lines.join('\n') + '\n';
}

function actionCell(entry: InventoryChangeEntry): string {
    if (entry.kind !== InventoryChangeKind.Availability) return entry.kind;
    return `availability:${entry.available ? 'published' : 'suspended'}`;
}

function defaultFormatTimestamp(ms: number): string {
    if (!Number.isFinite(ms)) return '';
    // `Number.isFinite` lets through values outside the legal `Date`
    // range (~±100M days from epoch). Those produce a `Date` whose
    // time value is `NaN`, and `toISOString()` then throws
    // `RangeError`, which would break the whole export. Guard
    // explicitly so a single bad timestamp downgrades to an empty cell.
    const date = new Date(ms);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString();
}
